'use client'
// Page constants
const header = 'Create Your Account'
const subheader = 'Sign up to begin'

// Internal modules
import { useCookie } from '@/utils/Session'

// Next modules
import Link from 'next/link'
import Image from 'next/image'
import { useRouter, useSearchParams } from 'next/navigation'

// React modules
import { useEffect, useState } from 'react'

// Components
import { ISessionContext, useSessionContext } from '@/app/_components/context'
import { Button, CheckBox, Input } from '@/app/_components/html'
import { note } from '@/utils/Note'
import { Login } from '@/templates/Login'

const Signup = () => {  
  const [ , setSessionEmail ] = useSessionContext() as ISessionContext

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [agree, setAgree] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const router = useRouter()
  const [loggedInCookie] = useCookie('loggedIn')

  if (loggedInCookie()) {
    router.push('/mappings');
  }

  const validateEmail = (email: string): boolean => {
    if (email.length === 0) {
      setErrorMsg('Email is required')
      return false
    }

    const emailParsed = email.match(/(.+)@(.+)\.(.+)/)

    if (!emailParsed?.[1] || !emailParsed?.[2] || !emailParsed?.[3]) {
      setErrorMsg('Email is invalid')
      return false
    }

    return true
  }

  const handleSignup = async () => {
    setErrorMsg('')
    note(`Signup attempt for ${email}`)

    if (!validateEmail(email)) {
      return false
    }

    if (password.length === 0) {
      setErrorMsg('Password is required')
      return false
    }

    if (password.length < 8) {
      setErrorMsg('Password must be at least 8 characters')
      return false
    }

    if (password !== confirmPassword) {
      setErrorMsg('Password and confirm password must match')
      return false
    }

    if (!agree) {
      setErrorMsg('You must agree to the terms of service')
      return false
    }

    setErrorMsg('')

    const resp = await fetch('/api/user', {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        password,
        agree,
      }),
    });

    if (resp.ok) {
      const user = await resp.json();

      if (user.email) {
        return router.push('/login/');
      }
    }

    setErrorMsg(await resp.text());
    return false;
  }

  return (
    <>
    <div className="flex md:hidden flex-row my-2 items-center justify-center">
      <Image 
        src={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/images/dessertdata/cake1.png`} 
        alt="cake logo" 
        width={50}
        height={50}
        className=""
      />
      <p className="text-xl font-bold mx-2">Dessert Data</p>
      <p className="text-lg bg-theme-brand-50 text-theme-brand-600 px-2 rounded-lg">Beta</p>
    </div>
    <main className='flex md:hidden flex-col text-center mt-20'>
      <h1 className='text-lg font-bold'>{header}</h1>
      <p className='text-theme-gray-600'>{subheader}</p>
      <form className='mx-5 mt-8'>
          <Input 
            id='signup-email'
            type='email' 
            value={email}
            placeholder='Email address' 
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input 
            id='signup-pw'
            type='password' 
            value={password}
            placeholder='Password'
            onChange={(e) => setPassword(e.target.value)}
          />
          <Input 
            id='signup-confirm'
            type='password' 
            placeholder='Confirm Password'
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <CheckBox
            id='signup-terms'
            checked={agree}
            className='text-sm pl-1'
            onChange={(e) => setAgree(e.target.checked)}
          >
            <div>
              I agree to the <Link href="/privacy-policy/" className="text-theme-brand-700 border-none hover:text-theme-brand-900">
              Privacy Policy</Link> and <Link href="/terms-and-conditions/" className="text-theme-brand-700 border-none hover:text-theme-brand-900">
              Terms and Conditions</Link>
            </div>
          </CheckBox>
          <Button 
            type="button"
            onClick={() => handleSignup()}
          >
            Create Account
          </Button>
        {errorMsg && <p className='text-theme-error-600 mt-3'>{errorMsg}</p>}
      </form>
      <div className='mx-5 mt-5 mb-5'>
        Or <Link href='/login/' className=''>Log In</Link>
      </div>
    </main>
    <main className='hidden md:flex flex-col text-center bg-theme-brand-600 h-screen'>
      <div className='w-1/2 m-auto bg-white rounded-3xl shadow-lg shadow-theme-gray-900'>
        <div className="flex flex-row my-2 justify-center items-center">
          <Image 
            src={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/images/dessertdata/cake1.png`} 
            alt="cake logo" 
            width={60}
            height={60}
            className=""
          />
          <p className="text-md bg-theme-brand-50 text-theme-brand-600 px-2 rounded-lg">Beta</p>
        </div>
        <h1 className='text-xl font-bold mt-8 text-theme-brand-900'>{header}</h1>
        <p>{subheader}</p>
        <form className='mx-5 mt-10'>
          <Input 
            id='signup-email'
            type='email' 
            placeholder='Email address'
            value={email} 
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input 
            id='signup-pw'
            type='password' 
            placeholder='Password'
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Input 
            id='signup-confirm'
            type='password' 
            placeholder='Confirm Password'
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <CheckBox
            id='signup-terms'
            checked={agree}
            className='text-sm pl-5'
            onChange={(e) => setAgree(e.target.checked)}
          >
            <div>
              I agree to the <Link href="/privacy-policy/" className="text-theme-brand-700 border-none hover:text-theme-brand-900">
              Privacy Policy</Link> and <Link href="/terms-and-conditions/" className="text-theme-brand-700 border-none hover:text-theme-brand-900">
              Terms and Conditions</Link>
            </div>
          </CheckBox>
          <Button 
            type="button"
            onClick={() => handleSignup()}
          >
            Create Account
          </Button>
          {errorMsg && <p className='text-theme-error-600 mt-3'>{errorMsg}</p>}
        </form>
        <div className='mx-5 mt-5 mb-5'>
          <Link href='/login/' className=''>Log In</Link>
        </div>
      </div>
    </main>
    </>
  )
};

export default Signup
